import React from 'react';
import {
  Check, Copy, Mic, MicOff,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import {
  Box, Center, HStack, Icon, Pressable, Switch, Text, VStack,
} from '@gluestack-ui/themed';

import { PREFLIGHT_COLUMN_TITLE_HEIGHT, PreflightColumnLabel } from './PreflightColumnLabel';

/** Side-column width from the pre-flight desk (`LecturerPreflightStage`). */
export const PREFLIGHT_SUPERVISORS_COLUMN_WIDTH = 288;

/** Full-width video tile, 16:9 of the column width. */
export const PREFLIGHT_SUPERVISOR_TILE_HEIGHT = 162;
/** Your own camera as picture-in-picture over the first colleague's video, 16:9. */
export const PREFLIGHT_SELF_PIP_WIDTH = 96;
export const PREFLIGHT_SELF_PIP_HEIGHT = 54;
const PREFLIGHT_SELF_PIP_INSET = 8;

export type PreflightSupervisorTile = {
  identity: string;
  node: React.ReactNode;
};

export type PreflightSupervisorsColumnLayoutProps = {
  hereCount: number;
  loading?: boolean;
  tiles: React.ReactNode;
  isEmpty?: boolean;
  micMuted: boolean;
  micError: string | null;
  onMicToggle: (enabled: boolean) => void;
  linkCopied: boolean;
  copyError: boolean;
  sittingLink: string;
  onCopyLink: () => void;
};

/** Shared supervisors column chrome for production LiveKit and Visual Lab paths. */
export function PreflightSupervisorsColumnLayout({
  hereCount,
  loading = false,
  tiles,
  isEmpty = false,
  micMuted,
  micError,
  onMicToggle,
  linkCopied,
  copyError,
  sittingLink,
  onCopyLink,
}: PreflightSupervisorsColumnLayoutProps) {
  const { t } = useTranslation();

  return (
    <VStack
      space="lg"
      width="100%"
      maxWidth={PREFLIGHT_SUPERVISORS_COLUMN_WIDTH}
      alignSelf="flex-start"
      testID="preflight-supervisors-column-root"
    >
      <HStack h={PREFLIGHT_COLUMN_TITLE_HEIGHT} justifyContent="space-between" alignItems="center">
        <PreflightColumnLabel testID="preflight-supervisors-column-title">
          {t('oralExam.lecturer.preflightReview.supervisors.title')}
        </PreflightColumnLabel>
        {!loading ? (
          <HStack space="xs" alignItems="center">
            <Box width={8} height={8} borderRadius="$full" bg="$statusSuccess" />
            <Text
              size="sm"
              color="$textSubtle"
              testID="preflight-supervisors-here-count"
            >
              {t('oralExam.lecturer.preflightReview.supervisors.hereCount', {
                count: hereCount,
                defaultValue: '{{count}} here',
              })}
            </Text>
          </HStack>
        ) : null}
      </HStack>

      {loading ? (
        <Box testID="preflight-supervisors-column-loading" p="$2">
          <Text size="sm" color="$textSubtle">
            {t('oralExam.lecturer.preflightReview.supervisors.connecting')}
          </Text>
        </Box>
      ) : (
        <>
          <VStack space="sm" testID="preflight-supervisors-column">
            {isEmpty ? (
              <Center minHeight={140} px="$3" testID="preflight-supervisors-empty">
                <Text size="sm" color="$textSubtle" textAlign="center">
                  {t('oralExam.lecturer.preflightReview.supervisors.empty')}
                </Text>
              </Center>
            ) : (
              tiles
            )}
          </VStack>

          <VStack>
            <Box>
              <HStack alignItems="center" space="sm" minHeight={36}>
                {micMuted || micError ? (
                  <Icon
                    as={MicOff}
                    size="sm"
                    color="$textSubtle"
                    testID="preflight-supervisor-mic-off"
                  />
                ) : (
                  <Icon
                    as={Mic}
                    size="sm"
                    color="$textSubtle"
                    testID="preflight-supervisor-mic-on"
                  />
                )}
                <Text size="sm" color="$textBody" flex={1}>
                  {t('oralExam.lecturer.preflightReview.supervisors.microphone', {
                    defaultValue: 'Microphone',
                  })}
                </Text>
                <Switch
                  size="sm"
                  value={!micMuted}
                  onValueChange={(enabled) => { onMicToggle(enabled); }}
                  testID="preflight-supervisor-mic-toggle"
                  accessibilityLabel={t('oralExam.lecturer.preflightReview.supervisors.microphone', {
                    defaultValue: 'Microphone',
                  })}
                />
              </HStack>
              {micError ? (
                <Text
                  size="xs"
                  color="$statusError"
                  mt="$1"
                  testID="preflight-supervisor-mic-error"
                >
                  {micError}
                </Text>
              ) : null}
            </Box>

            <Box>
              <Pressable
                onPress={onCopyLink}
                minHeight={36}
                justifyContent="center"
                testID="preflight-copy-co-supervisor-link"
              >
                <HStack space="sm" alignItems="center">
                  <Icon
                    as={linkCopied ? Check : Copy}
                    size="sm"
                    color="$textBrand"
                    testID="preflight-copy-co-supervisor-icon"
                  />
                  <Text
                    size="sm"
                    fontFamily="$heading"
                    color="$textBrand"
                    flexShrink={1}
                    numberOfLines={2}
                  >
                    {linkCopied
                      ? t('oralExam.lecturer.preflightReview.supervisors.linkCopied')
                      : t('oralExam.lecturer.preflightReview.supervisors.copyLink')}
                  </Text>
                </HStack>
              </Pressable>
              {copyError ? (
                <Text
                  size="xs"
                  color="$textSubtle"
                  selectable
                  mt="$1"
                  testID="preflight-co-supervisor-link-text"
                >
                  {sittingLink}
                </Text>
              ) : null}
            </Box>
          </VStack>
        </>
      )}
    </VStack>
  );
}

function PreflightSupervisorTileFrame({
  identity,
  children,
}: {
  identity: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      testID={`preflight-supervisor-tile-${identity}`}
      width="100%"
      height={PREFLIGHT_SUPERVISOR_TILE_HEIGHT}
      borderRadius="$lg"
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

/**
 * One full-width tile per colleague; your own camera sits as picture-in-picture in
 * the first colleague's corner. Alone, your camera is the full tile.
 */
export function PreflightSupervisorTiles({
  colleagues,
  self,
}: {
  colleagues: PreflightSupervisorTile[];
  self: PreflightSupervisorTile | null;
}) {
  if (colleagues.length === 0) {
    return self ? (
      <PreflightSupervisorTileFrame identity={self.identity}>{self.node}</PreflightSupervisorTileFrame>
    ) : null;
  }
  return (
    <VStack space="sm">
      {colleagues.map((colleague, index) => (
        <Box key={colleague.identity} position="relative">
          <PreflightSupervisorTileFrame identity={colleague.identity}>
            {colleague.node}
          </PreflightSupervisorTileFrame>
          {index === 0 && self ? (
            <Box
              position="absolute"
              right={PREFLIGHT_SELF_PIP_INSET}
              bottom={PREFLIGHT_SELF_PIP_INSET}
              width={PREFLIGHT_SELF_PIP_WIDTH}
              height={PREFLIGHT_SELF_PIP_HEIGHT}
              borderRadius="$md"
              borderWidth={2}
              borderColor="$bgCard"
              overflow="hidden"
              testID={`preflight-supervisor-tile-${self.identity}`}
            >
              {self.node}
            </Box>
          ) : null}
        </Box>
      ))}
    </VStack>
  );
}
